import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Card, Spin, Empty, Tag, Button, message } from "antd";
import {
  CalendarOutlined,
  EnvironmentOutlined,
  ApartmentOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import {
  JobApplicationStatusEnum,
  JobApplicationStatusLabel,
} from "Constants/Enums/JobApplicantStatusEnum";
import { JobPostingStatusEnum } from "Constants/Enums/JobPostingStatusEnum";

const ACCENT = "#0f5a2a";

// Color/border per pipeline status — green family for forward progress,
// red for reject, neutral for "Applied".
const STATUS_STYLES = {
  [JobApplicationStatusEnum.Applied]: { color: "#1f3a5f", bg: "#eef3fb", border: "#1f3a5f" },
  [JobApplicationStatusEnum.ShortList]: { color: "#0f5a2a", bg: "#e6f4ea", border: "#22c55e" },
  [JobApplicationStatusEnum.TermsAccepted]: { color: "#0f5a2a", bg: "#e6f4ea", border: "#22c55e" },
  [JobApplicationStatusEnum.InterviewInProgress]: {
    color: "#92400e",
    bg: "#fff7ed",
    border: "#f59e0b",
  },
  [JobApplicationStatusEnum.InterviewCompleted]: { color: "#1d4ed8", bg: "#eff6ff", border: "#3b82f6" },
  [JobApplicationStatusEnum.FeedbackAdded]: { color: "#1d4ed8", bg: "#eff6ff", border: "#3b82f6" },
  [JobApplicationStatusEnum.DocumentSend]: { color: "#5b21b6", bg: "#f5f3ff", border: "#7c3aed" },
  [JobApplicationStatusEnum.DocumentRecieved]: { color: "#5b21b6", bg: "#f5f3ff", border: "#7c3aed" },
  [JobApplicationStatusEnum.OnboardingDone]: { color: "#0f5a2a", bg: "#e6f4ea", border: "#22c55e" },
  [JobApplicationStatusEnum.Hired]: { color: "white", bg: ACCENT, border: ACCENT },
  [JobApplicationStatusEnum.Reject]: { color: "#991b1b", bg: "#fef2f2", border: "#ef4444" },
};

function MyApplicationsList() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchApps = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(API.hiringJobApplicant.myApplications);
      const data = res.data;
      const list = Array.isArray(data)
        ? data
        : data?.applications ||
          data?.applicants ||
          data?.myApplications ||
          data?.recommendations ||
          data?.items ||
          data?.result ||
          data?.results ||
          data?.data ||
          [];
      setApps(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error fetching my applications:", err);
      if (err.response?.status !== 404) {
        message.error("Could not load your applications.");
      }
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (apps.length === 0) {
    return (
      <Empty
        description="You haven't applied to any internal roles yet."
        style={{ padding: "40px 0" }}
      />
    );
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
        gap: 16,
      }}
    >
      {apps.map((app) => (
        <ApplicationCard
          key={app.jobApplicantId || app.id}
          app={app}
          onOpen={() =>
            navigate(
              `/employeedashboard/explore-careers/application/${app.jobApplicantId || app.id}`
            )
          }
        />
      ))}
    </div>
  );
}

function ApplicationCard({ app, onOpen }) {
  // Backend may return the pipeline status under several field names or as a
  // string ("Hired") instead of the numeric enum. Normalize so the badge
  // doesn't fall back to "Applied" when the real status is something else.
  const status = resolveStatus(app);
  // Per Mudassir (May-23): `isPastRole` is now present on all JobApplication
  // responses. When a HIRED application has isPastRole=true, the employee has
  // since been hired into a different role — show a muted "Past Role" badge
  // so the user can distinguish it from their current active role.
  const isPastHired =
    status === JobApplicationStatusEnum.Hired && Boolean(app.isPastRole);
  // Per Sara (May-25): when the JobPosting has been marked Filled by HR but
  // the applicant is still mid-pipeline (Applied / ShortList /
  // InterviewInProgress / etc.), the application is effectively dead-ended —
  // someone else got hired. Surface "Position Filled" instead of the
  // pipeline status so the user understands their app is no longer active,
  // and hide the "Action needed" prompt below (no action is possible).
  // Final states (Hired / Reject / Past Role) take priority over this.
  const isFinalState =
    status === JobApplicationStatusEnum.Hired ||
    status === JobApplicationStatusEnum.Reject;
  const isJobPostingFilled = app.status === JobPostingStatusEnum.Filled;
  const showFilledBadge =
    isJobPostingFilled && !isFinalState && !isPastHired;

  const style = isPastHired
    ? { color: "#475569", bg: "#f1f5f9", border: "#94a3b8" }
    : showFilledBadge
    ? { color: "#92400e", bg: "#fff7ed", border: "#fbbf24" }
    : STATUS_STYLES[status] || STATUS_STYLES[JobApplicationStatusEnum.Applied];
  const statusLabel = isPastHired
    ? "Past Role"
    : showFilledBadge
    ? "Position Filled"
    : JobApplicationStatusLabel[status] || "Pending";

  const title = app.jobTitle || app.forJobApply || "Applied Role";
  const department = app.departmentName || app.department;
  const location = app.jobLocationType || app.location;
  const appliedOn = app.appliedOn || app.createdOn || app.applicationDate;

  // Suppress the action prompt when the role is filled — picking a slot
  // wouldn't do anything because the posting is closed.
  const needsAction =
    status === JobApplicationStatusEnum.InterviewInProgress &&
    !isJobPostingFilled;

  return (
    <Card
      hoverable
      onClick={onOpen}
      style={{
        borderRadius: 18,
        borderLeft: `5px solid ${style.border}`,
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        cursor: "pointer",
      }}
      styles={{ body: { padding: 18 } }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 10,
          marginBottom: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {department && (
            <div
              style={{
                fontSize: "0.7rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                color: "#617e9e",
              }}
            >
              <ApartmentOutlined style={{ marginRight: 4 }} />
              {department}
            </div>
          )}
          <div
            style={{
              fontSize: "1.1rem",
              fontWeight: 700,
              color: ACCENT,
              marginTop: 2,
              lineHeight: 1.3,
            }}
          >
            {title}
          </div>
        </div>
        <span
          style={{
            background: style.bg,
            color: style.color,
            border: `1px solid ${style.border}`,
            padding: "4px 12px",
            borderRadius: 40,
            fontSize: "0.7rem",
            fontWeight: 700,
            whiteSpace: "nowrap",
            textTransform: "uppercase",
            letterSpacing: 0.3,
          }}
        >
          {statusLabel}
        </span>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
        {location && (
          <Tag color="green" style={{ borderRadius: 40, fontSize: "0.72rem" }}>
            <EnvironmentOutlined /> {location}
          </Tag>
        )}
        {appliedOn && (
          <Tag style={{ borderRadius: 40, fontSize: "0.72rem" }}>
            <CalendarOutlined /> Applied {formatDate(appliedOn)}
          </Tag>
        )}
      </div>

      {needsAction && (
        <div
          style={{
            marginTop: 12,
            padding: "10px 14px",
            background: "#fff7ed",
            borderLeft: "3px solid #f59e0b",
            borderRadius: 12,
            fontSize: "0.82rem",
            color: "#92400e",
            fontWeight: 600,
          }}
        >
          ⚡ Action needed — pick your interview slot inside.
        </div>
      )}

      {/* Position Filled hint — replaces the action prompt when HR has
          closed the posting. Lets the applicant know why no further
          actions are available without making them dig into the detail
          view first. */}
      {showFilledBadge && (
        <div
          style={{
            marginTop: 12,
            padding: "10px 14px",
            background: "#f8fafc",
            borderLeft: "3px solid #94a3b8",
            borderRadius: 12,
            fontSize: "0.82rem",
            color: "#475569",
            fontWeight: 500,
          }}
        >
          🔒 This role has been filled. Your application is no longer active.
        </div>
      )}

      <div style={{ marginTop: 14, textAlign: "right" }}>
        <Button
          type="link"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          style={{ color: ACCENT, fontWeight: 600, padding: 0 }}
        >
          View details <ArrowRightOutlined />
        </Button>
      </div>
    </Card>
  );
}

ApplicationCard.propTypes = {
  app: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return String(iso);
  }
}

// Resolve the pipeline status from whatever shape the backend returns.
// Per Mudassir (2026-05-20): `applicationStatus` is the JobApplicant pipeline
// status (Applied → Hired). `status` on this response is the JobPosting's
// own status (Open/Closed) and must NOT be used for the badge.
function resolveStatus(app) {
  const raw =
    app?.applicationStatus ??
    app?.jobApplicationStatus ??
    app?.jobApplicantStatus ??
    app?.currentStatus ??
    app?.pipelineStatus ??
    app?.statusEnum;
  if (raw == null) return null;
  if (typeof raw === "number") return raw;
  const asNum = Number(raw);
  if (!Number.isNaN(asNum) && JobApplicationStatusLabel[asNum] != null) return asNum;
  const text = String(raw).trim().toLowerCase();
  for (const [key, value] of Object.entries(JobApplicationStatusEnum)) {
    if (key.toLowerCase() === text) return value;
  }
  for (const [num, label] of Object.entries(JobApplicationStatusLabel)) {
    if (String(label).trim().toLowerCase() === text) return Number(num);
  }
  return null;
}

export default MyApplicationsList;
